import axios from 'axios';
import { refreshAccessToken } from '../services/AuthService';
import { getCookie, removeCookie, setCookie } from './CookieUtil';

const AUTH_COOKIE_NAMES = ['accessToken', 'refreshToken'];
const AUTH_REFRESH_LOCK_NAME = 'borine-auth-refresh';
let refreshPromise: Promise<string | null> | null = null;

interface JwtPayload {
  exp?: number;
}

const decodeBase64Url = (value: string) => {
  const base64 = value.replace(/-/g, '+').replace(/_/g, '/');
  const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
  return window.atob(padded);
};

const parseJwtPayload = (token: string): JwtPayload | null => {
  const payload = token.split('.')[1];
  if (!payload) {
    return null;
  }

  try {
    return JSON.parse(decodeBase64Url(payload)) as JwtPayload;
  } catch (error) {
    return null;
  }
};

export const clearAuthCookies = () => {
  // refreshToken은 서버의 HttpOnly 쿠키가 기본이며, 여기서는 이전 버전의 일반 쿠키만 정리한다.
  AUTH_COOKIE_NAMES.forEach(removeCookie);
};

export const storeAccessToken = (token: string) => {
  setCookie('accessToken', token, 1);
  removeCookie('refreshToken');
};

export const isAuthenticationError = (error: unknown) => {
  if (!axios.isAxiosError(error)) {
    return false;
  }
  return error.response?.status === 401 || error.response?.status === 403;
};

export const isAccessTokenValid = (token: string | null) => {
  if (!token) {
    return false;
  }

  const payload = parseJwtPayload(token);
  if (!payload?.exp) {
    return false;
  }

  return payload.exp * 1000 > Date.now();
};

export const hasValidAuthToken = () => {
  return isAccessTokenValid(getCookie('accessToken'));
};

export const getValidAccessToken = () => {
  const token = getCookie('accessToken');
  if (isAccessTokenValid(token)) {
    return token;
  }

  clearAuthCookies();
  return null;
};

const requestNewAccessToken = async () => {
  try {
    const data = await refreshAccessToken();
    if (!isAccessTokenValid(data.accessToken)) {
      clearAuthCookies();
      return null;
    }
    storeAccessToken(data.accessToken);
    return data.accessToken as string;
  } catch (error) {
    if (isAuthenticationError(error)) {
      clearAuthCookies();
      return null;
    }
    throw error;
  }
};

export const refreshAccessTokenOnce = () => {
  if (!refreshPromise) {
    const browserLocks = (navigator as any).locks;
    const refresh = browserLocks?.request
      ? browserLocks.request(AUTH_REFRESH_LOCK_NAME, async () => {
          // 다른 탭이 먼저 갱신했다면 공유된 accessToken 쿠키를 그대로 사용한다.
          const currentAccessToken = getCookie('accessToken');
          return isAccessTokenValid(currentAccessToken) ? currentAccessToken : requestNewAccessToken();
        })
      : requestNewAccessToken();

    refreshPromise = Promise.resolve(refresh).finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
};

export const getValidAccessTokenOrRefresh = async () => {
  const accessToken = getCookie('accessToken');
  if (isAccessTokenValid(accessToken)) {
    return accessToken;
  }
  return refreshAccessTokenOnce();
};
